import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const BotPerformanceCard = ({ botData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const {
    id = '',
    name = 'Trading Bot',
    exchange = 'Unknown',
    status = 'active',
    statusReason = '',
    winRate = 0,
    sharpeRatio = 0,
    maxDrawdown = 0,
    totalTrades = 0,
    profitLoss = 0,
    profitLossPercent = 0,
    lastUpdate = new Date()?.toISOString(),
    performance24h = 0,
    performance7d = 0,
    performance30d = 0,
    activePositions = 0,
    currentValue = 0,
    initialCapital = 0,
    profitAsset = 'USD',
    signals,
    usable,
    directionalAccuracy,
    netPnlRaw
  } = botData || {};

  const getStatusColor = () => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success';
      case 'paused':
        return 'bg-warning/10 text-warning';
      case 'halted':
        return 'bg-error/10 text-error';
      case 'testing':
        return 'bg-accent/10 text-accent';
      case 'error':
        return 'bg-error/10 text-error';
      case 'archived':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'active':
        return 'Play';
      case 'paused':
        return 'Pause';
      case 'halted':
        return 'AlertOctagon';
      case 'testing':
        return 'FlaskConical';
      case 'error':
        return 'AlertCircle';
      case 'archived':
        return 'Archive';
      default:
        return 'Circle';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'paused':
        return 'Paused';
      case 'halted':
        return 'Halted';
      case 'testing':
        return 'Testing';
      case 'error':
        return 'Error';
      case 'archived':
        return 'Archived';
      default:
        return status?.charAt(0)?.toUpperCase() + status?.slice(1);
    }
  };

  const getProfitLossColor = (value) => {
    if (value > 0) return 'text-success';
    if (value < 0) return 'text-error';
    return 'text-muted-foreground';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })?.format(value);
  };

  const formatPercent = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value?.toFixed(2)}%`;
  };

  const formatProfitLoss = (value) => {
    const sign = value > 0 ? '+' : '';
    if (profitAsset && profitAsset !== 'USD') {
      return `${sign}${Number(value || 0).toFixed(2)} ${profitAsset}`;
    }
    return formatCurrency(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const formatRawPnl = (value) => {
    return Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 6,
      maximumFractionDigits: 6
    });
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleViewDetails = () => {
    navigate(`/vault-detail-view/${id}`, { state: { botData } });
  };

  return (
    <div className="bg-card rounded-[10px] p-7 border border-border shadow-elevation-1 hover:shadow-elevation-2 transition-smooth">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-heading font-semibold text-foreground">
              {name}
            </h3>
            <span className={`px-2 py-1 rounded-md text-xs font-medium caption ${getStatusColor()}`}>
              <Icon name={getStatusIcon()} size={12} className="inline mr-1" />
              {getStatusLabel()}
            </span>
          </div>
          <p className="text-sm text-muted-foreground caption">{exchange}</p>
          {(status === 'halted' || status === 'testing' || status === 'paused' || status === 'archived') && statusReason && (
            <p className={`text-xs mt-1 ${
              status === 'testing' ? 'text-accent/80'
                : status === 'paused' ? 'text-warning/80'
                : status === 'archived' ? 'text-muted-foreground'
                : 'text-error/80'
            }`}>{statusReason}</p>
          )}
        </div>
        <button
          onClick={handleToggleExpand}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-smooth"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={18} />
        </button>
      </div>

      {/* Vault Value - Prominent Display */}
      <div className="mb-6 p-4 rounded-lg bg-primary/5 border border-primary/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground caption mb-1">Vault Value</p>
            <p className="text-2xl font-heading font-bold text-foreground data-text">
              {formatCurrency(currentValue)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground caption mb-1">Total Profit</p>
            <p className={`text-xl font-heading font-semibold data-text ${getProfitLossColor(profitLoss)}`}>
              {formatProfitLoss(profitLoss)}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground caption">Win Rate</p>
          <p className="text-xl font-heading font-semibold text-foreground data-text">
            {winRate?.toFixed(1)}%
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground caption">Sharpe Ratio</p>
          <p className="text-xl font-heading font-semibold text-foreground data-text">
            {sharpeRatio?.toFixed(2)}
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground caption">Max Drawdown</p>
          <p className="text-xl font-heading font-semibold text-error data-text">
            {maxDrawdown?.toFixed(1)}%
          </p>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground caption">Total Trades</p>
          <p className="text-xl font-heading font-semibold text-foreground data-text">
            {totalTrades?.toLocaleString()}
          </p>
        </div>
      </div>

      {status === 'testing' && typeof signals === 'number' && typeof usable === 'number' && typeof directionalAccuracy === 'number' && typeof netPnlRaw === 'number' && (
        <div className="mb-6 p-4 rounded-lg bg-accent/5 border border-accent/15">
          <h4 className="text-sm font-medium text-foreground mb-3">Testing Metrics</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground caption">Signals</p>
              <p className="text-lg font-semibold text-foreground data-text">{signals.toLocaleString()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground caption">Usable</p>
              <p className="text-lg font-semibold text-foreground data-text">{usable.toLocaleString()}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground caption">Directional Accuracy</p>
              <p className="text-lg font-semibold text-foreground data-text">{directionalAccuracy.toFixed(2)}%</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground caption">Net PnL (raw)</p>
              <p className="text-lg font-semibold text-success data-text">{formatRawPnl(netPnlRaw)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-muted-foreground caption">ROI</p>
          <p className={`text-lg font-heading font-semibold data-text ${getProfitLossColor(profitLossPercent)}`}>
            {formatPercent(profitLossPercent)}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground caption">Initial Capital</p>
          <p className="text-sm font-medium text-foreground data-text">
            {formatCurrency(initialCapital)}
          </p>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-6 pt-6 border-t border-border space-y-4 animate-slide-down">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground caption">24h</p>
              <p className={`text-sm font-medium data-text ${getProfitLossColor(performance24h)}`}>
                {formatPercent(performance24h)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground caption">7d</p>
              <p className={`text-sm font-medium data-text ${getProfitLossColor(performance7d)}`}>
                {formatPercent(performance7d)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground caption">30d</p>
              <p className={`text-sm font-medium data-text ${getProfitLossColor(performance30d)}`}>
                {formatPercent(performance30d)}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground caption">Active Positions</p>
            <p className="text-sm font-medium text-foreground data-text">{activePositions}</p>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground caption">Last Update</p>
            <p className="text-sm font-medium text-foreground data-text">{formatDate(lastUpdate)}</p>
          </div>

          <div className="flex gap-2 pt-2">
            <button 
              onClick={handleViewDetails}
              className="flex-1 h-9 px-4 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-smooth text-sm font-medium"
            >
              View Details
            </button>
            <button className="flex-1 h-9 px-4 rounded-lg bg-muted text-foreground hover:bg-muted/80 transition-smooth text-sm font-medium">
              Configure
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BotPerformanceCard;
